// useECharts — lazy-loaded Apache ECharts renderer untuk chat.
//
// ECharts cukup besar (~1MB), jadi di-dynamic-import hanya saat pertama kali
// sebuah pesan chat berisi blok ```echarts``` atau ```dashboard```. Pemanggilan
// berikutnya pakai instance modul yang sama.
//
// Dua entry point:
//   • renderEChart(container, option, {height, title})  → satu chart
//   • renderDashboard(rootBlock, spec)                   → KPI cards + banyak chart
//
// Semua sumber chart berasal dari teks model → HANYA JSON (tanpa fungsi JS),
// di-parse pemanggil dengan JSON.parse. Tema gelap diterapkan otomatis supaya
// model cukup menyuplai data tanpa mengurus warna.

let _echartsPromise = null

function loadECharts() {
  if (_echartsPromise) return _echartsPromise
  _echartsPromise = import('echarts')
    .then(mod => mod.default || mod)
    .catch(err => { _echartsPromise = null; throw err })
  return _echartsPromise
}

// ── Dark theme defaults ──────────────────────────────────────────────────────

const PALETTE = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#06b6d4', '#ec4899', '#84cc16']
const TEXT = '#cbd5e1'
const TEXT_STRONG = '#e2e8f0'
const AXIS = 'rgba(148,163,184,0.22)'
const SPLIT = 'rgba(148,163,184,0.10)'

function decorateAxis(axis) {
  if (Array.isArray(axis)) return axis.map(decorateAxis)
  return {
    axisLine: { lineStyle: { color: AXIS } },
    axisTick: { show: false },
    axisLabel: { color: TEXT, fontSize: 11 },
    splitLine: { lineStyle: { color: SPLIT } },
    nameTextStyle: { color: TEXT },
    ...axis,
  }
}

function hasPie(option) {
  const s = option.series
  const arr = Array.isArray(s) ? s : (s ? [s] : [])
  return arr.some(x => x && /pie|funnel|radar|gauge|sunburst|treemap/.test(x.type || ''))
}

function withDarkDefaults(raw) {
  const o = { ...raw }
  o.backgroundColor = o.backgroundColor || 'transparent'
  o.color = o.color || PALETTE
  o.textStyle = { color: TEXT, fontFamily: 'inherit', ...(o.textStyle || {}) }
  o.tooltip = o.tooltip || { trigger: hasPie(o) ? 'item' : 'axis' }
  o.grid = { left: '3%', right: '4%', bottom: '3%', top: o.title ? 46 : 26, containsLabel: true, ...(o.grid || {}) }
  if (o.legend) o.legend = { textStyle: { color: TEXT }, icon: 'roundRect', top: 6, ...(o.legend || {}) }
  if (o.title) o.title = { left: 'left', textStyle: { color: TEXT_STRONG, fontSize: 13, fontWeight: 600 }, ...(o.title || {}) }
  if (o.xAxis) o.xAxis = decorateAxis(o.xAxis)
  if (o.yAxis) o.yAxis = decorateAxis(o.yAxis)
  return o
}

// ── Render satu chart ────────────────────────────────────────────────────────

export async function renderEChart(container, rawOption, opts = {}) {
  const echarts = await loadECharts()
  const option = withDarkDefaults(rawOption || {})
  if (opts.title && !option.title) {
    option.title = { text: opts.title, left: 'left', textStyle: { color: TEXT_STRONG, fontSize: 13, fontWeight: 600 } }
  }
  const h = Number(opts.height) || 320
  container.style.height = h + 'px'
  container.style.width = '100%'
  const chart = echarts.init(container, null, { renderer: 'svg' })
  chart.setOption(option)
  let ro = null
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(() => { try { chart.resize() } catch { /* detached */ } })
    ro.observe(container)
  }
  return { chart, ro }
}

// ── Render dashboard (KPI cards + grid chart) ────────────────────────────────

function buildKpiCard(k) {
  const card = document.createElement('div')
  card.className = 'md-dash-kpi'
  const val = document.createElement('div')
  val.className = 'md-dash-kpi-val'
  val.textContent = k.value != null ? String(k.value) : '–'
  const label = document.createElement('div')
  label.className = 'md-dash-kpi-label'
  label.textContent = k.label || ''
  card.appendChild(val)
  card.appendChild(label)
  if (k.delta) {
    const d = document.createElement('div')
    const dir = k.dir === 'down' ? 'is-down' : k.dir === 'up' ? 'is-up' : 'is-flat'
    d.className = `md-dash-kpi-delta ${dir}`
    d.textContent = (k.dir === 'down' ? '▼ ' : k.dir === 'up' ? '▲ ' : '') + k.delta
    card.appendChild(d)
  }
  return card
}

export async function renderDashboard(rootBlock, spec) {
  rootBlock.innerHTML = ''
  const wrap = document.createElement('div')
  wrap.className = 'md-dash'

  if (spec.title) {
    const t = document.createElement('div')
    t.className = 'md-dash-title'
    t.textContent = spec.title
    wrap.appendChild(t)
  }

  if (Array.isArray(spec.kpis) && spec.kpis.length) {
    const grid = document.createElement('div')
    grid.className = 'md-dash-kpis'
    spec.kpis.forEach(k => grid.appendChild(buildKpiCard(k)))
    wrap.appendChild(grid)
  }

  const pending = []
  if (Array.isArray(spec.charts) && spec.charts.length) {
    const cgrid = document.createElement('div')
    cgrid.className = 'md-dash-charts'
    for (const c of spec.charts) {
      const cell = document.createElement('div')
      cell.className = 'md-dash-chart'
      if (c.span === 2 || c.full) cell.classList.add('is-full')
      if (c.title) {
        const ct = document.createElement('div')
        ct.className = 'md-dash-chart-title'
        ct.textContent = c.title
        cell.appendChild(ct)
      }
      const canvas = document.createElement('div')
      canvas.className = 'md-dash-chart-canvas'
      cell.appendChild(canvas)
      cgrid.appendChild(cell)
      if (c.option) pending.push({ el: canvas, option: c.option, height: c.height || 240 })
    }
    wrap.appendChild(cgrid)
  }

  rootBlock.appendChild(wrap)

  const handles = []
  for (const p of pending) {
    try { handles.push(await renderEChart(p.el, p.option, { height: p.height })) }
    catch { /* satu chart gagal jangan robohkan dashboard */ }
  }
  return handles
}
